import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { formatDistanceToNow, differenceInDays, format } from 'date-fns';

function StatCard({ label, value, color, sub, icon }) {
  return (
    <div className="stat-card">
      {icon && <div style={{ fontSize: 20, marginBottom: 2 }}>{icon}</div>}
      <div className="stat-label">{label}</div>
      <div className={`stat-value ${color || ''}`}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

const PLAN_BADGE = {
  free: 'badge-gray', trial: 'badge-orange', plus: 'badge-blue', family: 'badge-purple',
};

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [customers, setCustomers] = useState([]);
  const [recentSignups, setRecentSignups] = useState([]);
  const [issues, setIssues] = useState([]);
  const [devices, setDevices] = useState([]);
  const [kids, setKids] = useState([]);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [recentScreenshots, setRecentScreenshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      const [
        { data: cust },
        { data: iss },
        { data: allKids },
        { data: devData },
        { count: totalKids },
        { data: alerts10 },
        { data: ss10 },
      ] = await Promise.all([
        supabase.from('customers').select('*').order('created_at', { ascending: false }),
        supabase.from('issues').select('*').order('created_at', { ascending: false }).limit(5),
        supabase.from('kids').select('*').order('created_at', { ascending: false }),
        supabase.from('devices').select('*').order('last_seen_at', { ascending: false }),
        supabase.from('kids').select('*', { count: 'exact', head: true }),
        supabase.from('alerts').select('*').order('timestamp', { ascending: false }).limit(10),
        supabase.from('screenshots').select('id,device_name,parent_id,timestamp').order('timestamp', { ascending: false }).limit(10),
      ]);

      const custData = cust || [];
      setCustomers(custData);
      setRecentSignups(custData.slice(0, 10));
      setIssues(iss || []);
      setKids(allKids || []);
      setDevices(devData || []);
      setRecentAlerts(alerts10 || []);
      setRecentScreenshots(ss10 || []);

      // Stats
      const total = custData.length;
      const paid = custData.filter(c => c.plan === 'plus' || c.plan === 'family').length;
      const trials = custData.filter(c => c.plan === 'trial').length;
      const freeUsers = custData.filter(c => c.plan === 'free').length;
      const expiring = custData.filter(c => {
        if (c.plan !== 'trial' || !c.trial_ends_at) return false;
        return differenceInDays(new Date(c.trial_ends_at), new Date()) <= 3;
      }).length;
      const revenue = custData.reduce((sum, c) => {
        if (c.plan === 'plus') return sum + 499;
        if (c.plan === 'family') return sum + 799;
        return sum;
      }, 0);
      const openIss = (iss || []).filter(i => i.status === 'open').length;
      const activeKids = (allKids || []).filter(k => k.is_active !== false).length;
      const pausedKids = (allKids || []).filter(k => k.is_active === false).length;
      const onlineDevices = (devData || []).filter(d =>
        d.device_name && d.last_seen_at && (Date.now() - new Date(d.last_seen_at).getTime()) < 5 * 60 * 1000
      ).length;

      setStats({
        total, paid, trials, freeUsers, expiring, revenue, openIss,
        totalKids: totalKids || 0, activeKids, pausedKids, onlineDevices,
        totalDevices: (devData || []).filter(d => d.device_name).length,
      });
      setLoading(false);
    })();
  }, []);

  // Build kids map for signups display
  const kidsMap = {};
  kids.forEach(k => {
    if (!kidsMap[k.customer_id]) kidsMap[k.customer_id] = [];
    kidsMap[k.customer_id].push(k);
  });

  // Search filter
  const searchResults = search.trim() ? (() => {
    const q = search.toLowerCase();
    const matchedCustomers = customers.filter(c =>
      c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.phone?.includes(q)
    );
    const matchedKids = kids.filter(k =>
      k.kid_name?.toLowerCase().includes(q) || k.kid_username?.toLowerCase().includes(q)
    );
    return { customers: matchedCustomers.slice(0, 5), kids: matchedKids.slice(0, 5) };
  })() : null;

  if (loading) return <div style={{ color: '#64748b', padding: 40 }}>Loading dashboard...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title">📊 Admin Dashboard</h1>
        {/* Quick search */}
        <input type="text" placeholder="🔍 Search parent or @username..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ width: 300, padding: '8px 14px', fontSize: 13, borderRadius: 8, border: '1px solid #e2e8f0' }} />
      </div>

      {/* Search results */}
      {searchResults && (
        <div className="card" style={{ marginBottom: 20, padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 10 }}>
            Search Results
          </div>
          {searchResults.customers.length === 0 && searchResults.kids.length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: 13 }}>No results for "{search}"</p>
          ) : (
            <>
              {searchResults.customers.map(c => (
                <div key={c.id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18 }}>👤</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{c.email} · {c.phone}</div>
                  </div>
                  <span className={`badge ${PLAN_BADGE[c.plan]}`} style={{ marginLeft: 'auto' }}>{c.plan}</span>
                </div>
              ))}
              {searchResults.kids.map(k => (
                <div key={k.id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18 }}>👶</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{k.kid_name}</div>
                    <div style={{ fontSize: 11, color: '#0ea5e9', fontFamily: 'monospace' }}>@{k.kid_username}</div>
                  </div>
                  <span className={`badge ${k.is_active !== false ? 'badge-green' : 'badge-gray'}`} style={{ marginLeft: 'auto' }}>
                    {k.is_active !== false ? 'Active' : 'Paused'}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Stat cards - Row 1: Business */}
      <div className="stat-row">
        <StatCard icon="👥" label="Total Parents" value={stats.total} color="blue" />
        <StatCard icon="💰" label="Paid Plans" value={stats.paid} color="green" sub={`₹${stats.revenue?.toLocaleString('en-IN')}/mo`} />
        <StatCard icon="⏰" label="Active Trials" value={stats.trials} color="yellow" />
        <StatCard icon="🆓" label="Free Users" value={stats.freeUsers} color="gray" />
        <StatCard icon="⚠️" label="Expiring Soon" value={stats.expiring} color={stats.expiring > 0 ? 'red' : 'green'} sub="Within 3 days" />
      </div>

      {/* Stat cards - Row 2: Kids & Devices */}
      <div className="stat-row" style={{ marginTop: 0 }}>
        <StatCard icon="👶" label="Total Kids" value={stats.totalKids} color="blue" />
        <StatCard icon="🟢" label="Active Kids" value={stats.activeKids} color="green" />
        <StatCard icon="⏸️" label="Paused Kids" value={stats.pausedKids} color={stats.pausedKids > 0 ? 'yellow' : 'green'} />
        <StatCard icon="🖥️" label="Devices" value={stats.totalDevices} color="blue" />
        <StatCard icon="📡" label="Online Now" value={stats.onlineDevices} color={stats.onlineDevices > 0 ? 'green' : 'gray'} />
        <StatCard icon="🎫" label="Open Issues" value={stats.openIss} color={stats.openIss > 0 ? 'red' : 'green'} />
      </div>

      {/* Revenue card */}
      <div className="card" style={{ background: 'linear-gradient(135deg, #0f172a, #1e3a5f)', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Monthly Revenue
            </div>
            <div style={{ fontSize: 36, fontWeight: 900, color: '#fff' }}>
              ₹{stats.revenue?.toLocaleString('en-IN') || 0}
              <span style={{ fontSize: 14, color: '#64748b', fontWeight: 400, marginLeft: 8 }}>/month</span>
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              {stats.paid} paid · {stats.trials} trials · ₹{(stats.revenue * 12)?.toLocaleString('en-IN')}/year
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#38bdf8' }}>{customers.filter(c => c.plan === 'plus').length}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>Plus @ ₹499</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#a78bfa' }}>{customers.filter(c => c.plan === 'family').length}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>Family @ ₹799</div>
            </div>
          </div>
          {/* Conversion funnel */}
          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: '12px 16px' }}>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 6 }}>CONVERSION FUNNEL</div>
            <div style={{ fontSize: 12, color: '#cbd5e1' }}>
              {stats.total} signups → {stats.trials} trials → {stats.paid} paid
              <span style={{ color: stats.paid > 0 ? '#22c55e' : '#64748b', fontWeight: 700, marginLeft: 8 }}>
                {stats.total > 0 ? Math.round(stats.paid / stats.total * 100) : 0}% conversion
              </span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>
        {/* Recent signups */}
        <div className="card">
          <div className="card-title">🆕 Recent Signups</div>
          {recentSignups.length === 0
            ? <p style={{ fontSize: 13, color: '#94a3b8' }}>No signups yet.</p>
            : recentSignups.map(c => {
                const cKids = kidsMap[c.id] || [];
                return (
                  <div key={c.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
                      <span className={`badge ${PLAN_BADGE[c.plan] || 'badge-gray'}`}>{c.plan}</span>
                      {c.phone && (
                        <a href={`https://wa.me/${c.phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
                          style={{ marginLeft: 'auto', fontSize: 11, color: '#22c55e', textDecoration: 'none' }}>
                          💬
                        </a>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {c.email} · {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                    </div>
                    {cKids.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                        {cKids.map(k => (
                          <span key={k.id} style={{
                            background: k.is_active !== false ? '#dcfce7' : '#f1f5f9',
                            color: k.is_active !== false ? '#166534' : '#94a3b8',
                            padding: '2px 8px', borderRadius: 99, fontSize: 11, fontFamily: 'monospace',
                          }}>
                            @{k.kid_username}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
          }
        </div>

        {/* Activity feed */}
        <div className="card">
          <div className="card-title">⚡ Recent Activity</div>
          {recentAlerts.length === 0 && recentScreenshots.length === 0
            ? <p style={{ fontSize: 13, color: '#94a3b8' }}>No activity yet.</p>
            : (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>Latest Alerts</div>
                {recentAlerts.slice(0, 5).map(a => (
                  <div key={a.id} style={{ padding: '6px 0', borderBottom: '1px solid #f8fafc', fontSize: 12 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                      <span>{a.type === 'urgent' ? '🚨' : a.type === 'warning' ? '⏰' : 'ℹ️'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#374151', fontSize: 12, lineHeight: 1.4 }}>{a.message?.slice(0, 80)}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>
                          {formatDistanceToNow(new Date(a.timestamp), { addSuffix: true })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginTop: 14, marginBottom: 6 }}>Latest Screenshots</div>
                {recentScreenshots.slice(0, 5).map(s => (
                  <div key={s.id} style={{ padding: '6px 0', borderBottom: '1px solid #f8fafc', fontSize: 12, display: 'flex', gap: 6 }}>
                    <span>📸</span>
                    <div>
                      <span style={{ fontWeight: 500 }}>{s.device_name || 'Unknown device'}</span>
                      <span style={{ color: '#94a3b8', fontSize: 10, marginLeft: 6 }}>
                        {formatDistanceToNow(new Date(s.timestamp), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )
          }
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18, marginTop: 18 }}>
        {/* Recent issues */}
        <div className="card">
          <div className="card-title">🎫 Recent Issues</div>
          {issues.length === 0
            ? <p style={{ fontSize: 13, color: '#94a3b8' }}>No issues. 🎉</p>
            : issues.map(i => (
              <div key={i.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span className={`badge ${i.priority === 'urgent' ? 'badge-red' : i.priority === 'high' ? 'badge-orange' : 'badge-yellow'}`}
                  style={{ flexShrink: 0, marginTop: 2 }}>
                  {i.priority}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{i.title}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    {i.customer_name} · {formatDistanceToNow(new Date(i.created_at), { addSuffix: true })}
                  </div>
                </div>
                <span className={`badge ${i.status === 'resolved' ? 'badge-green' : i.status === 'in_progress' ? 'badge-blue' : 'badge-yellow'}`}>
                  {i.status}
                </span>
              </div>
            ))
          }
        </div>

        {/* Devices overview */}
        <div className="card">
          <div className="card-title">🖥️ Connected Devices</div>
          {devices.filter(d => d.device_name).length === 0
            ? <p style={{ fontSize: 13, color: '#94a3b8' }}>No devices connected yet.</p>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {devices.filter(d => d.device_name).slice(0, 8).map(d => {
                  const isOnline = d.last_seen_at && (Date.now() - new Date(d.last_seen_at).getTime()) < 5 * 60 * 1000;
                  const kid = kids.find(k => k.id === d.kid_id);
                  const parent = customers.find(c => c.auth_id === d.parent_id);
                  return (
                    <div key={d.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', background: '#f8fafc',
                      border: '1px solid #e2e8f0', borderRadius: 8,
                    }}>
                      <div style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: isOnline ? '#22c55e' : '#94a3b8',
                        animation: isOnline ? 'pulse 2s infinite' : 'none',
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{d.device_name}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>
                          {kid ? `👶 ${kid.kid_name} (@${kid.kid_username})` : ''}
                          {parent ? ` · 👤 ${parent.name}` : ''}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, color: isOnline ? '#22c55e' : '#94a3b8' }}>
                        {isOnline ? '🟢 Online' : d.last_seen_at ? formatDistanceToNow(new Date(d.last_seen_at), { addSuffix: true }) : 'Never'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      </div>

      {/* Platform Roadmap */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ marginBottom: 16 }}>Platform Roadmap</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
          {[
            { icon: '🍎', name: 'macOS', detail: 'Apple Silicon & Intel', status: 'live', color: '#22c55e' },
            { icon: '🪟', name: 'Windows', detail: 'Windows 10 & 11', status: 'coming soon', color: '#f59e0b' },
            { icon: '🤖', name: 'Android', detail: 'Phone & Tablet App', status: 'coming soon', color: '#f59e0b' },
            { icon: '📱', name: 'iOS', detail: 'iPhone & iPad App', status: 'coming soon', color: '#f59e0b' },
            { icon: '💻', name: 'Chromebook', detail: 'Chrome OS', status: 'planned', color: '#94a3b8' },
            { icon: '🌐', name: 'Browser Extension', detail: 'Chrome & Firefox', status: 'planned', color: '#94a3b8' },
          ].map(p => (
            <div key={p.name} style={{
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 10, padding: '16px 14px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>{p.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{p.name}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>{p.detail}</div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                background: `${p.color}20`, color: p.color,
              }}>
                {p.status.toUpperCase()}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Coming Soon Features */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ marginBottom: 4 }}>Feature Roadmap</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
          Planned features across all platforms
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
          {[
            { icon: '📍', title: 'Location Tracking', platform: 'Android · iOS', priority: 'high' },
            { icon: '📵', title: 'App Blocking', platform: 'All platforms', priority: 'high' },
            { icon: '📲', title: 'Call & SMS Monitor', platform: 'Android', priority: 'high' },
            { icon: '🔐', title: 'Remote Lock', platform: 'All platforms', priority: 'high' },
            { icon: '📺', title: 'YouTube History', platform: 'All platforms', priority: 'medium' },
            { icon: '🗺️', title: 'Geofencing', platform: 'Android · iOS', priority: 'medium' },
            { icon: '💬', title: 'Social Media Monitor', platform: 'Android · iOS', priority: 'medium' },
            { icon: '🆘', title: 'SOS Panic Button', platform: 'Android · iOS', priority: 'medium' },
            { icon: '🎥', title: 'Screen Recording', platform: 'macOS · Windows', priority: 'medium' },
            { icon: '👁️', title: 'Live Screen View', platform: 'All platforms', priority: 'low' },
            { icon: '🔇', title: 'Silent Mode', platform: 'Android · iOS', priority: 'low' },
            { icon: '📋', title: 'Clipboard Monitor', platform: 'macOS · Windows', priority: 'low' },
            { icon: '🔔', title: 'Push Notifications', platform: 'Admin & Dashboard', priority: 'high' },
            { icon: '📊', title: 'Advanced Analytics', platform: 'Dashboard', priority: 'medium' },
            { icon: '🧾', title: 'Billing & Payments', platform: 'Admin', priority: 'high' },
          ].map(f => (
            <div key={f.title} style={{
              background: f.priority === 'high' ? '#fef2f2' : f.priority === 'medium' ? '#fffbeb' : '#f8fafc',
              border: `1px solid ${f.priority === 'high' ? '#fecaca' : f.priority === 'medium' ? '#fde68a' : '#e2e8f0'}`,
              borderRadius: 10, padding: '12px 14px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 18 }}>{f.icon}</span>
                <span style={{
                  fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 99,
                  background: f.priority === 'high' ? '#dc2626' : f.priority === 'medium' ? '#f59e0b' : '#94a3b8',
                  color: '#fff',
                }}>
                  {f.priority.toUpperCase()}
                </span>
              </div>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#0f172a', marginBottom: 2 }}>{f.title}</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>{f.platform}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
